import { useCallback, useEffect, useMemo, useState } from 'react';
import Table from 'components/Table';
import {
  numberToCurrencyString,
  dateToLabel,
  numberToDisplayString,
} from 'further-ui/utils';
import { Box, Tooltip } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import AccruedFeesCell from './AccruedFeesCell';
import PerformanceFeeCell from './PerformanceFeeCell';
import TotalSaleValueCell from './TotalSaleValueCell';
import AccruedFeesPercentageAllocationCell from './AccruedFeesPercentageAllocationCell';
import SharePriceCell from './SharePriceCell';
import ClearColumnButton from './ClearColumnButton';
import NetFigureDisplay from './NetFigureDisplay';
import api from 'lib/trpcClient';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import ReviewShareholdingsTableFooter from './ReviewShareholdingsTableFooter';
import { CompareArrows } from '@mui/icons-material';
import { createInvestmentViewLink } from 'adminConstants/routes';
import { round } from 'lodash';
import SharesAllocatedForSaleCell from './SharesAllocatedForSaleCell';
import { StagedShareholding } from '../ExitDetailsForm/types';
import {
  stageAmendmentForCorrection,
  stageAmendment,
  stageCorrectionForOriginal,
  stageNewExit,
} from './helpers';
import type { Mutation as StageAmendmentForCorrectionMutation } from './helpers/stage-amendment-for-correction';
import type { Mutation as StageCorrectionForOriginalMutation } from './helpers/stage-correction-for-original';
import type { Mutation as StageAmendmentMutation } from './helpers/stage-amendment';
import type { Mutation as StageNewExitMutation } from './helpers/stage-new-exit';

const useStyles = makeStyles()((theme) => ({
  trancheColumn: {
    minWidth: '7rem',
  },
  allocationDateWithShareClassColumn: {
    minWidth: '12em',
  },
  tableHeadingTr: {
    borderBottom: '1px solid #EDEDED',
  },
  tableHeadingTd: {
    color: 'rgba(0, 0, 0, 0.6)',
    padding: '18px 6px 15px',
    position: 'relative',
    fontSize: '14px',
    borderBottom: '0 none',
  },
  tooltipLink: {
    color: theme.palette.text.rootColor,
  },
}));

type Props = any; //TODO: define types

const ReviewShareholdings: React.FC<Props> = ({
  exitDetails,
  previousExitDetails,
  shareholdings,
  setShareholdings,
  disableTable,
  isBeingEdited,
  isBeingCorrected = false,
  exitId,
  maintainOriginalExitDetails,
  exitAmendmentId = '',
}) => {
  const { classes } = useStyles();
  const handleMutation = useHandleTrpcMutation();
  const mutationOptions = handleMutation({ invalidations: [] });
  const [accruedFeesPercentageAllocation, setAccruedFeesPercentageAllocation] =
    useState<number | null>(exitDetails.chargeAccruedFees ? 100 : 0);
  const [areInlineAccruedFeesSet, setAreInlineAccruedFeesSet] = useState(false);

  const stageForCreationMutation =
    api.exit.stageForCreation.useMutation(mutationOptions);
  const stageForEditMutation =
    api.exit.stageForEdit.useMutation(mutationOptions);
  const stageForCorrectionMutation =
    api.exit.stageForCorrection.useMutation(mutationOptions);
  const stageAmendmentForCorrectionMutation =
    api.exit.stageAmendmentForCorrection.useMutation(mutationOptions);

  useEffect(() => {
    if (!exitDetails || Object.keys(exitDetails).length === 0) {
      return;
    }

    // Clear the table before fetching new data (except for amendment correction: keep previous data until we have success, so changing date doesn't briefly show empty)
    const isAmendmentCorrection = exitId && isBeingEdited && isBeingCorrected;
    if (!isAmendmentCorrection) {
      setShareholdings([]);
    }

    const sharedShareConsiderationFields = {
      shareConsiderationCompanyId: exitDetails.shareConsiderationCompanyId,
      shareConsiderationShareClassId:
        exitDetails.shareConsiderationShareClassId,
      totalConsiderationShares: exitDetails.totalConsiderationShares
        ? Number(exitDetails.totalConsiderationShares)
        : undefined,
      shareConsiderationValuePerShare:
        exitDetails.shareConsiderationValuePerShare
          ? Number(exitDetails.shareConsiderationValuePerShare)
          : undefined,
      shareConsiderationValueTotal: exitDetails.shareConsiderationValueTotal
        ? Number(exitDetails.shareConsiderationValueTotal)
        : undefined,
      shareConsiderationEisEligible: exitDetails.shareConsiderationEisEligible,
      shareConsiderationAllocationMethod:
        exitDetails.shareConsiderationAllocationMethod,
      permitPartialShareAllocation: exitDetails.permitPartialShareAllocation,
    };

    if (exitId && isBeingEdited && isBeingCorrected) {
      stageAmendmentForCorrection(
        stageAmendmentForCorrectionMutation as StageAmendmentForCorrectionMutation,
        exitId,
        exitAmendmentId ?? '',
        exitDetails,
        previousExitDetails,
        sharedShareConsiderationFields,
        maintainOriginalExitDetails,
        setInitialShareholdings,
      );
    } else if (exitId && isBeingEdited && !isBeingCorrected) {
      stageAmendment(
        stageForEditMutation as StageAmendmentMutation,
        exitId,
        exitDetails,
        previousExitDetails,
        sharedShareConsiderationFields,
        setInitialShareholdings,
      );
    } else if (exitId && !isBeingEdited) {
      stageCorrectionForOriginal(
        stageForCorrectionMutation as StageCorrectionForOriginalMutation,
        exitId,
        exitDetails,
        sharedShareConsiderationFields,
        maintainOriginalExitDetails,
        setInitialShareholdings,
      );
    } else if (!exitId && isBeingEdited) {
      stageAmendment(
        stageForEditMutation as StageAmendmentMutation,
        exitId,
        exitDetails,
        previousExitDetails,
        sharedShareConsiderationFields,
        setInitialShareholdings,
      );
    } else if (!exitId && !isBeingEdited) {
      stageNewExit(
        stageForCreationMutation as StageNewExitMutation,
        exitDetails,
        setInitialShareholdings,
      );
    }
  }, [
    exitDetails,
    isBeingEdited,
    isBeingCorrected,
    exitId,
    previousExitDetails,
    maintainOriginalExitDetails,
  ]);

  const getAccruedFeesAutoValue = (sh: StagedShareholding) => {
    if (accruedFeesPercentageAllocation === 0) {
      return {
        accruedFeesCharged: 0,
        accruedFeesSetBelowTarget: false,
      };
    }

    const investorFees = Number(sh.investorTotalAccruedFees) || 0;
    const targetAccruedFeesCharged =
      investorFees * ((accruedFeesPercentageAllocation ?? 0) / 100);
    const baseValue =
      Number(
        isBeingEdited ? sh.additionalGrossReceivedValue : sh.totalSaleValue,
      ) || 0;
    const perfFee = Number(sh.performanceFee) || 0;
    const maximumAccruedFees = round(baseValue - round(perfFee, 2), 2);
    const maximumAccruedFeesAboveZero = Math.max(maximumAccruedFees, 0);

    if (targetAccruedFeesCharged > maximumAccruedFeesAboveZero) {
      return {
        accruedFeesCharged: round(maximumAccruedFeesAboveZero, 2),
        accruedFeesSetBelowTarget: true,
      };
    }

    return {
      accruedFeesCharged: round(targetAccruedFeesCharged, 2),
      accruedFeesSetBelowTarget: false,
    };
  };

  const setInitialShareholdings = (input: Array<StagedShareholding>) => {
    const newShareholdings = input.map((sh) => {
      const shares = Number(sh.sharesAllocatedForSale) || 0;
      // Use taxableValueSharePrice from API response for calculating gross taxable value
      const taxPrice = Number(sh.taxableValueSharePrice) || 0;
      const grossTaxableValue = round(shares * taxPrice, 2);

      // For amendment corrections (when totalAvailableAccruedFees is present), preserve original accruedFeesCharged
      // For new exits, trust the backend's optimal allocation - don't recalculate
      // Only recalculate if user manually adjusts the percentage allocation via UI
      const { accruedFeesCharged, accruedFeesSetBelowTarget } =
        sh.totalAvailableAccruedFees !== undefined
          ? {
              accruedFeesCharged: sh.accruedFeesCharged,
              accruedFeesSetBelowTarget: false,
            }
          : {
              accruedFeesCharged: sh.accruedFeesCharged ?? 0,
              accruedFeesSetBelowTarget: false,
            };

      const totalSaleValue = Number(sh.totalSaleValue) || 0;
      const perfFee = Number(sh.performanceFee) || 0;
      let totalToBeReturned = round(
        totalSaleValue - (accruedFeesCharged ?? 0) - perfFee,
        2,
      );

      if (sh.previousAccruedFeesCharged || sh.previousPerformanceFeesCharged) {
        const prevAccrued = Number(sh.previousAccruedFeesCharged) || 0;
        const prevPerf = Number(sh.previousPerformanceFeesCharged) || 0;
        totalToBeReturned -= prevAccrued + prevPerf;
      }

      return {
        ...sh,
        totalSaleValue,
        // Keep taxableValueSharePrice from API response
        grossTaxableValue,
        netTaxableValue: grossTaxableValue,
        totalToBeReturned,
        accruedFeesCharged,
        accruedFeesSetBelowTarget,
        // Store original accruedFeesCharged for amendment corrections to use in cap calculation
        originalAccruedFeesCharged: sh.accruedFeesCharged,
      };
    });
    setShareholdings(newShareholdings);
  };

  const setOverride = useCallback(
    (shareholdingId, overrideProperties) => {
      setShareholdings((prevState) => {
        return prevState.map((sh) => {
          if (sh.shareholdingId === shareholdingId) {
            const updatedSh = {
              ...sh,
              ...overrideProperties,
            };

            if (
              Object.prototype.hasOwnProperty.call(
                overrideProperties,
                'sharesAllocatedForSale',
              )
            ) {
              const shares = Number(updatedSh.sharesAllocatedForSale) || 0;
              const price = Number(updatedSh.sharePrice) || 0;
              updatedSh.totalSaleValue = round(shares * price, 2);
            }

            if (
              Object.prototype.hasOwnProperty.call(
                overrideProperties,
                'taxableValueSharePrice',
              ) ||
              Object.prototype.hasOwnProperty.call(
                overrideProperties,
                'sharesAllocatedForSale',
              ) ||
              Object.prototype.hasOwnProperty.call(
                overrideProperties,
                'totalSaleValue',
              )
            ) {
              const shares = Number(updatedSh.sharesAllocatedForSale) || 0;
              const taxPrice = Number(updatedSh.taxableValueSharePrice) || 0;
              updatedSh.grossTaxableValue = round(shares * taxPrice, 2);
            }

            if (
              Object.prototype.hasOwnProperty.call(
                overrideProperties,
                'accruedFeesCharged',
              )
            ) {
              setAreInlineAccruedFeesSet(true);
              setAccruedFeesPercentageAllocation(null);
            }

            // as a failsafe, ensure that grossTaxableValue is never less than the received value
            updatedSh.grossTaxableValueBeingConstrained =
              updatedSh.totalSaleValue > updatedSh.grossTaxableValue;
            updatedSh.grossTaxableValue = Math.max(
              updatedSh.grossTaxableValue,
              updatedSh.totalSaleValue,
            );

            if ('performanceFee' in overrideProperties) {
              const totalSale = Number(updatedSh.totalSaleValue) || 0;
              const perfFee = Number(updatedSh.performanceFee) || 0;
              const maxAccruedFees = Math.max(totalSale - perfFee, 0);
              if ((updatedSh.accruedFeesCharged || 0) > maxAccruedFees) {
                updatedSh.accruedFeesCharged = round(maxAccruedFees, 2);
                updatedSh.accruedFeesSetBelowTarget = true;
              }
            }

            const totalSale = round(Number(updatedSh.totalSaleValue) || 0, 2);
            const accruedFees = Number(updatedSh.accruedFeesCharged) || 0;
            const perfFee = Number(updatedSh.performanceFee) || 0;
            const totalToBeReturned = totalSale - accruedFees - perfFee;

            const record = {
              ...updatedSh,
              totalSaleValue: totalSale,
              totalToBeReturned,
              netTaxableValue: updatedSh.grossTaxableValue,
            };

            if (
              sh.previousGrossReceivedValue !== undefined &&
              sh.previousGrossReceivedValue !== null
            ) {
              const totalSaleValue = Number(updatedSh.totalSaleValue) || 0;
              const previousGrossReceivedValue =
                Number(sh.previousGrossReceivedValue) || 0;
              const rawAdditional = round(
                totalSaleValue - previousGrossReceivedValue,
                2,
              );
              // In correction mode, additional received value must not be negative (it's the difference from previous amendment to this one)
              record.additionalGrossReceivedValue =
                rawAdditional < 0 ? 0 : rawAdditional;
            }

            if (
              sh.previousAccruedFeesCharged ||
              sh.previousPerformanceFeesCharged
            ) {
              record.totalToBeReturned -=
                sh.previousAccruedFeesCharged +
                sh.previousPerformanceFeesCharged;
            }

            return record;
          }
          return sh;
        });
      });
    },
    [setShareholdings, isBeingCorrected],
  );

  useEffect(() => {
    if (accruedFeesPercentageAllocation === null) return;

    const newShareholdings = shareholdings.map((sh) => {
      const { accruedFeesCharged, accruedFeesSetBelowTarget } =
        getAccruedFeesAutoValue(sh);
      const totalSaleValue = Number(sh.totalSaleValue) || 0;
      const perfFee = Number(sh.performanceFee) || 0;
      const totalToBeReturned = totalSaleValue - accruedFeesCharged - perfFee;

      return {
        ...sh,
        accruedFeesCharged,
        accruedFeesSetBelowTarget,
        totalToBeReturned,
        netTaxableValue: sh.grossTaxableValue,
      };
    });
    setShareholdings(newShareholdings);
    setAreInlineAccruedFeesSet(false);
  }, [accruedFeesPercentageAllocation]);

  const subscriptionAccruedFeeErrors = useMemo(() => {
    const totals = new Map<string, { charged: number; max: number }>();
    for (const sh of shareholdings) {
      if (!sh.investmentId) continue;
      const investmentId = String(sh.investmentId);
      const maxAccruedFees =
        sh.totalAvailableAccruedFees !== undefined
          ? (sh.originalAccruedFeesCharged ?? sh.accruedFeesCharged ?? 0) +
            (sh.totalAvailableAccruedFees ?? 0)
          : sh.investorTotalAccruedFees ?? 0;
      if (!totals.has(investmentId)) {
        totals.set(investmentId, { charged: 0, max: maxAccruedFees });
      }
      totals.get(investmentId)!.charged += sh.accruedFeesCharged ?? 0;
    }
    const result = new Map<string, { exceedsLimit: boolean }>();
    for (const sh of shareholdings) {
      if (!sh.investmentId) continue;
      const entry = totals.get(String(sh.investmentId));
      if (entry) {
        result.set(sh.shareholdingId, {
          exceedsLimit: entry.charged > entry.max,
        });
      }
    }
    return result;
  }, [shareholdings]);

  const columns = useMemo(
    () => [
      {
        label: 'Investor name',
        key: 'investorName',
        sort: false,
        render: (elm) => {
          if (!elm.sourceInvestmentId) return elm.investorName;

          const sourceInvestorName = elm?.transferSourceInvestor?.fullName;
          return (
            <Tooltip
              title={
                <div>
                  This investor is the recipient of a transfer from{' '}
                  <a
                    className={classes.tooltipLink}
                    href={createInvestmentViewLink(elm?.sourceInvestmentId)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {sourceInvestorName}
                  </a>{' '}
                  on {dateToLabel(elm?.transferDate)}.
                </div>
              }
            >
              <Box alignItems="center" display="flex" gap={4}>
                {elm?.investorName}
                <CompareArrows />
              </Box>
            </Tooltip>
          );
        },
      },

      {
        label: 'Tranche',
        key: 'trancheName',
        sort: false,
        className: classes.trancheColumn,
        render: (elm) => elm?.tranche?.fundName,
      },
      {
        label: 'Allocation',
        key: 'allocationDateWithShareClass',
        className: classes.allocationDateWithShareClassColumn,
        sort: false,
        render: (elm) => (
          <>
            {dateToLabel(elm.allocationDate)}
            <br />({elm?.shareClass})
          </>
        ),
      },
      {
        label: 'Shares held',
        key: 'noOfShares',
        sort: false,
        render: (elm) =>
          numberToDisplayString(elm?.noOfShares, {
            maximumFractionDigits: 10,
          }),
      },
      {
        label: 'Shares for sale',
        key: 'sharesAllocatedForSale',
        sort: false,
        render: (elm) =>
          isBeingEdited ? (
            numberToDisplayString(elm.sharesAllocatedForSale, {
              maximumFractionDigits: 10,
            })
          ) : (
            <SharesAllocatedForSaleCell
              cell={elm}
              setOverride={setOverride}
              disableTable={disableTable}
            />
          ),
      },
      {
        label: 'Received value',
        key: 'totalSaleValue',
        sort: false,
        tooltipText: (
          <>
            This is the capital returned and attributable to each individual,
            based on the transaction’s current <i>Received value share price</i>
            .
          </>
        ),
        render: (elm) => (
          <TotalSaleValueCell
            setOverride={setOverride}
            cell={elm}
            disableTable={disableTable}
          />
        ),
      },
      ...(isBeingEdited
        ? [
            {
              label: 'Additional received value',
              key: 'additionalGrossReceivedValue',
              sort: false,
              render: (elm) =>
                numberToCurrencyString(elm.additionalGrossReceivedValue),
            },
          ]
        : []),
      {
        label: isBeingEdited ? 'Additional performance fee' : 'Performance fee',
        key: 'performanceFee',
        sort: false,
        render: (elm) => (
          <PerformanceFeeCell
            shareholdingId={elm.shareholdingId}
            totalSaleValue={
              isBeingEdited
                ? elm.additionalGrossReceivedValue
                : elm.totalSaleValue
            }
            setOverride={setOverride}
            performanceFee={elm.performanceFee}
            disableTable={disableTable}
            fromUpload={elm._fromUpload || false}
          />
        ),
      },
      {
        label: isBeingEdited ? 'Additional accrued fees' : 'Accrued fees',
        key: 'accruedFeesCharged',
        sort: false,
        render: (elm) => (
          <AccruedFeesCell
            cell={elm}
            setOverride={setOverride}
            disableTable={disableTable}
            subscriptionError={subscriptionAccruedFeeErrors.get(
              elm.shareholdingId,
            )}
          />
        ),
      },
      {
        label: 'Net return',
        key: 'totalToBeReturned',
        sort: false,
        tooltipText: (
          <>
            This is the <i>Received value</i>, net of any accrued or performance
            fees charged.
          </>
        ),
        render: (elm) => (
          <NetFigureDisplay
            value={elm.totalToBeReturned}
            row={elm}
            isBeingEdited={isBeingEdited}
          />
        ),
      },
      {
        label: 'Taxable value share price',
        key: 'taxableValueSharePrice',
        sort: false,
        tooltipText: (
          <>
            This is the transaction’s <i>Taxable value share price</i>, which is
            always the same or more than the transaction’s{' '}
            <i>Received value share price.</i>
          </>
        ),
        render: (elm) => (
          <SharePriceCell
            cell={elm}
            setOverride={setOverride}
            disableTable={disableTable}
          />
        ),
      },
      {
        label: 'Net taxable value',
        key: 'netTaxableValue',
        sort: false,
        tooltipText: (
          <>
            <p>
              This figure is the transaction's taxable value share price
              multiplied by the number of shares sold.
            </p>
            <br />
            <p>
              If this figure is italicised, it is being calculated based on the
              row's received value figure, because it is higher than the row's
              stated taxable value share price.
            </p>
          </>
        ),
        render: (elm) => (
          <NetFigureDisplay
            value={elm.netTaxableValue}
            row={elm}
            isBeingEdited={isBeingEdited}
            isBeingConstrained={elm.grossTaxableValueBeingConstrained}
          />
        ),
      },
    ],
    [setOverride, disableTable, subscriptionAccruedFeeErrors],
  );

  const tableHeading = (
    <tr className={classes.tableHeadingTr}>
      <td></td>
      <td></td>
      <td></td>
      <td></td>
      <td>
        {!isBeingEdited && (
          <ClearColumnButton
            keyToClear="sharesAllocatedForSale"
            setShareholdings={setShareholdings}
            disable={disableTable}
          />
        )}
      </td>
      <td></td>
      {isBeingEdited && <td></td>}
      <td>
        <ClearColumnButton
          keyToClear="performanceFee"
          setShareholdings={setShareholdings}
          disable={disableTable}
        />
      </td>
      <td className={classes.tableHeadingTd}>
        <AccruedFeesPercentageAllocationCell
          disableTable={disableTable}
          areInlineAccruedFeesSet={areInlineAccruedFeesSet}
          accruedFeesPercentageAllocation={accruedFeesPercentageAllocation}
          setAccruedFeesPercentageAllocation={
            setAccruedFeesPercentageAllocation
          }
        />
      </td>
    </tr>
  );

  const isLoading =
    stageForCreationMutation.isPending ||
    stageForEditMutation.isPending ||
    stageForCorrectionMutation.isPending ||
    stageAmendmentForCorrectionMutation.isPending;

  return (
    <Table
      columns={columns}
      tablebody={shareholdings}
      loading={isLoading}
      CustomTableHeading={tableHeading}
      TableFooter={
        <ReviewShareholdingsTableFooter
          shareholdings={shareholdings}
          isBeingEdited={isBeingEdited}
        />
      }
      emptyMessage="No shareholdings found for this company. Possibly the shareholdings did not exist before the selected exit date."
      variant="nohover"
    />
  );
};

export default ReviewShareholdings;
